import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Alert, Spinner, ProgressBar } from 'react-bootstrap';

const SingleKPIPrompt = ({ show, onHide, onSubmit, parameter, loading = false, currentIndex = 0, totalParameters = 1 }) => {
  const [value, setValue] = useState(parameter?.default || 0);
  const [error, setError] = useState('');

  // Reset input value when parameter changes (clear text box for next parameter)
  useEffect(() => {
    if (parameter) {
      console.log('🔄 Parameter changed, clearing input and setting default:', parameter.label, parameter.default);
      setValue(parameter.default || 0);
      setError('');
    }
  }, [parameter]);

  const handleInputChange = (inputValue) => {
    const numericValue = parseFloat(inputValue) || 0;
    setValue(numericValue);
    
    // Clear error when user starts typing
    if (error) {
      setError('');
    }
  };

  const validateInput = () => {
    if (!parameter) return false;
    
    if (value === undefined || value === null || isNaN(value)) {
      setError(`${parameter.label} is required`);
      return false;
    }
    
    if (value < parameter.min || value > parameter.max) {
      setError(`${parameter.label} must be between ${parameter.min} and ${parameter.max}`);
      return false;
    }
    
    return true;
  };

  const handleSubmit = () => {
    if (validateInput()) {
      console.log('✅ Submitting KPI value:', value, 'for', parameter.label);
      onSubmit(value);
      // Note: The useEffect will handle clearing the input when parameter changes
    }
  };

  const handleUseDefault = () => {
    console.log('🔄 Using default value:', parameter?.default, 'for', parameter?.label);
    setValue(parameter?.default || 0);
    setError('');
  };

  if (!parameter) return null;

  return (
    <Modal show={show} onHide={onHide} size="md" backdrop="static" centered>
      <Modal.Header closeButton={!loading}>
        <Modal.Title>
          🔧 {parameter.label} Required
          <small className="text-muted d-block mt-1">
            Step {currentIndex + 1} of {totalParameters}
          </small>
        </Modal.Title>
      </Modal.Header>
      
      <Modal.Body>
        {/* Progress Bar */}
        <div className="mb-3">
          <ProgressBar 
            now={((currentIndex + 1) / totalParameters) * 100} 
            label={`${currentIndex + 1}/${totalParameters}`}
            variant="primary"
            style={{ height: '25px' }}
          />
          <small className="text-muted">
            Parameter collection progress
          </small>
        </div>

        <Alert variant="info" className="mb-4">
          <Alert.Heading>📊 Critical Process Parameter</Alert.Heading>
          <p className="mb-2">
            <strong>{parameter.label}</strong> is required for accurate bioprocess calculations.
          </p>
          <p className="mb-0 text-muted">
            {parameter.description || `This parameter affects engineering calculations like tip speed, power input, and mass transfer.`}
          </p>
        </Alert>

        <Form>
          <Form.Group className="mb-3">
            <Form.Label>
              <strong>Enter {parameter.label}</strong>
            </Form.Label>
            <Form.Control
              type="number"
              value={value}
              onChange={(e) => handleInputChange(e.target.value)}
              min={parameter.min}
              max={parameter.max}
              step={parameter.step || 0.01}
              isInvalid={!!error}
              disabled={loading}
              autoFocus
              className="text-center fw-bold"
              style={{ fontSize: '1.1rem' }}
            />
            {error && (
              <Form.Control.Feedback type="invalid">
                {error}
              </Form.Control.Feedback>
            )}
            <Form.Text className="text-muted">
              <div className="d-flex justify-content-between mt-2">
                <span>Range: {parameter.min} - {parameter.max}</span>
                <span>Default: {parameter.default}</span>
              </div>
            </Form.Text>
          </Form.Group>
        </Form>

        {error && (
          <Alert variant="danger" className="mt-3">
            <strong>Input Error:</strong> {error}
          </Alert>
        )}
      </Modal.Body>
      
      <Modal.Footer>
        <Button 
          variant="outline-secondary" 
          onClick={handleUseDefault}
          disabled={loading}
          size="sm"
        >
          Use Default ({parameter.default})
        </Button>
        <Button 
          variant="secondary" 
          onClick={onHide}
          disabled={loading}
        >
          Cancel
        </Button>
        <Button 
          variant="primary" 
          onClick={handleSubmit}
          disabled={loading}
          size="lg"
        >
          {loading ? (
            <>
              <Spinner size="sm" className="me-2" />
              Processing...
            </>
          ) : (
            currentIndex + 1 === totalParameters ? 'Start Calculations 🚀' : `Next Parameter ➡️ (${currentIndex + 2}/${totalParameters})`
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default SingleKPIPrompt;
